import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { RedesignButton } from '@components/RedesignButton';
import { Text } from '@components/Text';
import { Input } from '@components/Input';
import { Checkbox } from '@components/Checkbox';
import { Dropdown } from '@components/Dropdown';
import { fetchAttendeeTickets } from 'src/redesign/features/Attendees/store/actions';
import { attendeeTicketsSelector } from 'src/redesign/features/Attendees/store/selectors';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import S from './CancelTransferModal.module.scss';
import moment from 'moment';
import { siteEventsSelector } from '../../Events/store/selectors';
import { fetchEventsBySite } from '../../Events/store/actions';
import { attendeeEventsSidebarActions } from '../store/reducer';

interface CancelTransferModalProps {
  ticketInfo: any;
  onCancel?: (data: { refund: boolean; reason: string }) => void;
  onTransfer?: (data: { eventId: string; occurrenceId?: string; reason: string }) => void;
}

export const CancelTransferModal: React.FC<CancelTransferModalProps> = ({
  ticketInfo,
  onCancel,
  onTransfer,
}) => {
  const [mode, setMode] = useState<'select' | 'cancel' | 'transfer'>('select');
  const [cancelReason, setCancelReason] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [includeRefund, setIncludeRefund] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [selectedOccurrence, setSelectedOccurrence] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const siteEvents = useAppSelector(siteEventsSelector);

  useEffect(() => {
    // Fetch latest events for the same site when modal opens
    const siteId = ticketInfo?.site_id;
    if (siteId) {
      dispatch(fetchEventsBySite(siteId));
    }
  }, [dispatch, ticketInfo?.site_id]);

  // Filter events and get available occurrences
  const availableEvents = (siteEvents || []).filter(
    (event) => event.id !== ticketInfo.event.id && event.status === 'live',
  );

  const selectedEventData = availableEvents.find((event) => event.id === selectedEvent);
  const availableOccurrences = selectedEventData?.availableOccurrences || [];

  const handleCancel = async () => {
    if (!cancelReason.trim()) return;

    const actions = attendeeEventsSidebarActions;

    setIsSubmitting(true);
    try {
      if (onCancel) {
        await onCancel({
          refund: includeRefund,
          reason: cancelReason.trim(),
        });
      }
      // Refresh attendee tickets after successful cancellation
      dispatch(fetchAttendeeTickets());
      dispatch(closeCurrentModalAction());
      dispatch(actions.showAttendeeEventsSidebar());
    } catch (error) {
      console.error('Cancel failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTransfer = async () => {
    const actions = attendeeEventsSidebarActions;

    if (!transferReason.trim() || !selectedEvent) return;

    // If the selected event has occurrences, one must be selected
    if (availableOccurrences.length > 0 && !selectedOccurrence) return;

    setIsSubmitting(true);
    try {
      if (onTransfer) {
        await onTransfer({
          eventId: selectedEvent,
          occurrenceId: selectedOccurrence || undefined,
          reason: transferReason.trim(),
        });
      }
      // Refresh attendee tickets after successful transfer
      dispatch(fetchAttendeeTickets());
      dispatch(closeCurrentModalAction());
      dispatch(actions.showAttendeeEventsSidebar());
    } catch (error) {
      console.error('Transfer failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setMode('select');
    setCancelReason('');
    setTransferReason('');
    setIncludeRefund(false);
    setSelectedEvent('');
    setSelectedOccurrence('');
  };

  if (mode === 'select') {
    return (
      <div className={S.modalContent}>
        <Text variation="body" color="black-400" className={S.description}>
          Choose an action for your ticket to "{ticketInfo.event.title}"
        </Text>

        <div className={S.actionButtons}>
          <RedesignButton
            variation="secondary"
            height={52}
            onClick={() => setMode('cancel')}
            className={S.actionButton}
          >
            <Text regular color="brand-400">
              Cancel Registration
            </Text>
          </RedesignButton>

          <RedesignButton
            variation="primary"
            height={52}
            onClick={() => setMode('transfer')}
            className={S.actionButton}
          >
            Transfer to Another Event
          </RedesignButton>
        </div>
      </div>
    );
  }

  if (mode === 'cancel') {
    return (
      <div className={S.modalContent}>
        <Text variation="h4" bold className={S.title}>
          Cancel Event
        </Text>
        <Text variation="body" color="black-400" className={S.description}>
          Please provide a reason for canceling your ticket.
        </Text>

        <div className={S.formField}>
          <Text variation="body-small" bold className={S.fieldLabel}>
            Reason for Cancellation *
          </Text>
          <Input
            value={cancelReason}
            textAreaProps={{
              onChange: (e) => setCancelReason(e.target.value),
            }}
            placeholder="Please explain why you're canceling..."
            as="textarea"
          />
        </div>

        {ticketInfo.total > 0 ? (
          <div className={S.checkboxField}>
            <Checkbox
              checked={includeRefund}
              onChange={(e) => setIncludeRefund(e.checked)}
              label="Request Refund"
            />
            <Text variation="body-small" color="black-400">
              Check this box if you would like to request a refund for this cancellation.
            </Text>
          </div>
        ) : (
          <div className={S.infoMessage}>
            <Text variation="body-small" color="black-400">
              You will be marked as not attending this event. Since this was a free ticket, no
              refund is applicable.
            </Text>
          </div>
        )}

        <div className={S.modalActions}>
          <RedesignButton
            variation="secondary"
            height={52}
            onClick={handleBack}
            disabled={isSubmitting}
          >
            <Text regular color="brand-400">
              Back
            </Text>
          </RedesignButton>

          <RedesignButton
            variation="primary"
            height={52}
            onClick={handleCancel}
            disabled={!cancelReason.trim() || isSubmitting}
          >
            <Text regular color="white-100">
              {isSubmitting ? 'Processing...' : 'Submit Cancellation'}
            </Text>
          </RedesignButton>
        </div>
      </div>
    );
  }

  if (mode === 'transfer') {
    return (
      <div className={S.modalContent}>
        <Text variation="h4" bold className={S.title}>
          Transfer to Another Event
        </Text>
        <Text variation="body" color="black-400" className={S.description}>
          Select an event to transfer your ticket to and provide a reason. Your transfer request
          will be sent to the host for approval.
        </Text>

        <div className={S.formField}>
          <Text variation="body-small" bold className={S.fieldLabel}>
            Select Event *
          </Text>
          <Dropdown
            value={selectedEvent}
            onChange={(e) => {
              setSelectedEvent(e.value);
              setSelectedOccurrence(''); // Reset occurrence when event changes
            }}
            options={availableEvents.map((event) => ({
              label: event.title,
              value: event.id,
            }))}
            placeholder="Choose an event..."
          />
        </div>

        {availableOccurrences.length > 0 && selectedEvent && (
          <div className={S.formField}>
            <Text variation="body-small" bold className={S.fieldLabel}>
              {selectedEventData?.isTimeBased ? 'Select Time Slot *' : 'Select Date *'}
            </Text>
            <Dropdown
              value={selectedOccurrence}
              onChange={(e) => setSelectedOccurrence(e.value)}
              options={availableOccurrences.map((occurrence) => ({
                label: moment(occurrence.start_date)
                  .tz(occurrence.timezone)
                  .format('MMMM Do h:mm A'),
                value: occurrence.id,
              }))}
              placeholder={
                selectedEventData?.isTimeBased ? 'Choose a time slot...' : 'Choose a date...'
              }
            />
          </div>
        )}

        <div className={S.formField}>
          <Text variation="body-small" bold className={S.fieldLabel}>
            Reason for Transfer *
          </Text>
          <Input
            value={transferReason}
            textAreaProps={{
              onChange: (e) => setTransferReason(e.target.value),
            }}
            placeholder="Please explain why you're transferring..."
            as="textarea"
          />
        </div>

        <div className={S.modalActions}>
          <RedesignButton
            variation="secondary"
            height={52}
            onClick={handleBack}
            disabled={isSubmitting}
          >
            <Text regular color="brand-400">
              Back
            </Text>
          </RedesignButton>

          <RedesignButton
            variation="primary"
            height={52}
            onClick={handleTransfer}
            disabled={
              !transferReason.trim() ||
              !selectedEvent ||
              (availableOccurrences.length > 0 && !selectedOccurrence) ||
              isSubmitting
            }
          >
            <Text regular color="white-100">
              {isSubmitting ? 'Processing...' : 'Submit Transfer'}
            </Text>
          </RedesignButton>
        </div>
      </div>
    );
  }

  return null;
};
